import {
  Text,
  IconLightningBolt,
  GreyGrey30,
  GreyGrey60,
  GreyGrey80,
  IndigoIndigo50,
  Primary12Regular,
  Primary12SemiBold,
  RedRed80,
  Tooltip,
  Flex
} from '@sequencehq/core-components'
import { FC, useMemo } from 'react'
import { MetricGraph } from './MetricGraph'
import {
  FPS_ALERT_THRESHOLD,
  FPS_TARGET,
  formatFps
} from './performanceMonitorBar.constants'
import { MetricSample } from './performanceMonitorBar.types'
import { useFps } from './useFps'

type FpsItemProps = {
  isExpanded: boolean
}

const FPS_TOOLTIP_LABEL = 'Frame rate (max 60 fps)'

export const FpsItem: FC<FpsItemProps> = ({ isExpanded }) => {
  const { fps, history } = useFps()

  const isBelowThreshold = fps !== null && fps < FPS_ALERT_THRESHOLD

  const accentColor = isBelowThreshold ? RedRed80 : IndigoIndigo50
  const valueColor = isBelowThreshold ? RedRed80 : GreyGrey80
  const iconColor = isBelowThreshold ? RedRed80 : GreyGrey80

  const metricHistory = useMemo<MetricSample[]>(
    () =>
      history.map(sample => ({
        timestamp: sample.timestamp,
        value: sample.value
      })),
    [history]
  )

  if (!isExpanded) {
    return (
      <Flex flexDirection="column" alignItems="center" gap="6px" width="100%">
        <Tooltip label={FPS_TOOLTIP_LABEL} placement="right" hasArrow>
          <IconLightningBolt color={iconColor} />
        </Tooltip>
        {fps !== null && (
          <Text {...Primary12SemiBold} color={valueColor}>
            {Math.round(fps)}
          </Text>
        )}
      </Flex>
    )
  }

  return (
    <Flex flexDirection="column" gap="12px" paddingX="12px" width="100%">
      <Flex alignItems="center" gap="6px">
        <Tooltip label={FPS_TOOLTIP_LABEL} placement="right" hasArrow>
          <IconLightningBolt size={14} color={iconColor} />
        </Tooltip>
        <Text {...Primary12SemiBold} color={GreyGrey80}>
          Frame rate
        </Text>
      </Flex>
      <MetricGraph
        history={metricHistory}
        yMax={FPS_TARGET}
        threshold={FPS_ALERT_THRESHOLD}
        accentColor={accentColor}
        borderColor={GreyGrey30}
        isOverThreshold={isBelowThreshold}
        formatValue={formatFps}
      />
      <Flex flexDirection="column" gap="4px">
        <Text {...Primary12Regular} color={GreyGrey60}>
          Current: {fps !== null ? formatFps(fps) : '–'}
        </Text>
        <Text {...Primary12Regular} color={GreyGrey60}>
          Target: {formatFps(FPS_TARGET)}
        </Text>
      </Flex>
      {isBelowThreshold && (
        <Text {...Primary12SemiBold} color={RedRed80}>
          ⚠ Below {FPS_ALERT_THRESHOLD} fps
        </Text>
      )}
    </Flex>
  )
}
